"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Calendar, ChevronRight, Plane, Plus, LogIn } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useSession } from "next-auth/react";
import TripCard from "@/components/TripCard";

interface Trip {
  id: string;
  destination: string;
  startDate: string;
  endDate: string;
  createdAt: string;
}

export default function MyTrips() {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { data: session, status } = useSession();


  useEffect(() => {
    const fetchTrips = async () => {
      if (status !== "authenticated") {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch("/api/trips");
        if (!response.ok) throw new Error("Failed to fetch trips");
        const data = await response.json();
        setTrips(data);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load your trips",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTrips();
  }, [toast, status]);

  // Show loading state
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4 pt-8 sm:pt-24">
        <div className="space-y-4">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            My Trips
          </h1>
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  // Show login prompt for unauthenticated users
  if (status === "unauthenticated") {
    return (
      <div className="w-full min-h-screen flex items-center justify-center p-4">
        <div className="w-full min-w-4xl flex flex-col items-center justify-center p-8">
          <div className="p-4 rounded-full bg-indigo-50 mb-6">
            <Plane className="w-8 h-8 text-indigo-600 transform -rotate-45" />
          </div>
          <h2 className="text-2xl font-bold mb-3 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Welcome to Trip Planner
          </h2>
          <p className="text-gray-600 mb-8 text-center max-w-sm">
            Sign in to start planning your next adventure!
          </p>
          <div>
            <Link href="/api/auth/signin">
              <Button className="h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-base font-medium px-6">
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state for authenticated users with no trips
  if (trips.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-4">
        <div className="p-4 rounded-full bg-indigo-50 mb-6">
          <Plane className="w-8 h-8 text-indigo-600 transform -rotate-45" />
        </div>
        <h2 className="text-2xl font-bold mb-3 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
          Ready to Start Traveling?
        </h2>
        <p className="text-gray-600 mb-8 text-center max-w-sm">
          Create your first trip plan and let us help you organize your perfect
          adventure!
        </p>
        <Link href="/create-trip">
          <Button className="h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-lg font-medium px-6">
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Trip
          </Button>
        </Link>
      </div>
    );
  }

  // Show trips list for authenticated users with trips
  return (
    <div className="max-w-4xl mx-auto p-4 space-y-8 pt-8 sm:pt-24 pb-20">
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            My Trips
          </h1>
        </div>
        {/* <Link href="/create-trip">
          <Button className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700">
            <Plus className="w-4 h-4 mr-2" />
            New Trip
          </Button>
        </Link> */}
      </div>

      <div className="grid gap-4">
        {trips.map((trip) => (
          <TripCard key={trip.id} trip={trip} />
        ))}
      </div>
    </div>
  );
}
